import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSeoMeta } from "@unhead/react";
import toast from "react-hot-toast";
import {Loader2, Mail, Lock, ShieldCheck} from 'lucide-react';
import { lumi } from "@/lib/lumi";

type Mode = "login" | "register" | "reset";

const AUTH_ERRORS: Record<number, string> = {
  2254: "This email cannot be used to register.",
  2259: "This account is not active.",
  2261: "Email login is currently unavailable.",
  2262: "Google login is currently unavailable.",
};

function authErrorMessage(error: unknown): string {
  const code = (error as { data?: { code?: number } })?.data?.code;
  if (code && AUTH_ERRORS[code]) return AUTH_ERRORS[code];
  const message = error instanceof Error ? error.message : "";
  return message || "Authentication failed. Please try again.";
}

export default function Auth() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const redirectTo = params.get("redirect") || "/alerts";
  const initialMode = (params.get("mode") as Mode) || "login";

  const [mode, setMode] = useState<Mode>(
    initialMode === "register" || initialMode === "reset" ? initialMode : "login"
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userName, setUserName] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [loading, setLoading] = useState(false);

  useSeoMeta({
    title: "Sign in for Job Alerts | MineralJobs Board",
    description:
      "Create a MineralJobs Board account to receive automated email alerts when new FIFO, mineral, and entry level mining jobs are posted.",
  });

  const startCooldown = () => {
    setCooldown(60);
    const timer = window.setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const sendOtp = async () => {
    if (!email) {
      toast.error("Enter your email first.");
      return;
    }
    if (cooldown > 0) return;
    try {
      await lumi.auth.sendOtp({
        email,
        type: mode === "reset" ? "resetPassword" : "register",
      });
      setOtpSent(true);
      startCooldown();
      toast.success("Verification code sent. Check your inbox.");
    } catch (err) {
      console.error("sendOtp failed:", err);
      toast.error(authErrorMessage(err));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await lumi.auth.signInWithPassword({ email, password });
        toast.success("Signed in.");
        window.location.href = redirectTo;
        return;
      }
      if (mode === "register") {
        if (!otpSent || !otp) {
          toast.error("Send and enter the verification code first.");
          return;
        }
        await lumi.auth.signUpWithPassword({ email, password, otp, userName });
        toast.success("Account created.");
        window.location.href = redirectTo;
        return;
      }
      if (!otpSent || !otp) {
        toast.error("Send and enter the verification code first.");
        return;
      }
      await lumi.auth.resetPassword({ email, newPassword: password, otp });
      toast.success("Password updated. You are now signed in.");
      window.location.href = redirectTo;
    } catch (err) {
      console.error("Auth submit failed:", err);
      toast.error(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <div className="min-h-screen pt-16 flex items-center justify-center px-4" style={{ background: "#111827" }}>
        <div className="w-full max-w-md rounded-2xl border border-white/10 p-6 sm:p-8" style={{ background: "#1a1f2e" }}>
          <p className="text-xs uppercase tracking-wider mb-2" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>
            Job Alert Access
          </p>
          <h1 className="text-3xl font-black text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            {mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Reset password"}
          </h1>
          <p className="text-sm text-gray-400 mb-6">
            Lumi email alerts can only be sent to registered MineralJobs Board users.
          </p>

          <div className="flex gap-2 mb-6">
            {(["login", "register", "reset"] as Mode[]).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setMode(item);
                  setOtp("");
                  setOtpSent(false);
                }}
                className={`px-3 py-1.5 rounded-lg text-xs uppercase tracking-wider ${
                  mode === item ? "text-white" : "text-gray-500 hover:text-gray-300"
                }`}
                style={{
                  background: mode === item ? "rgba(249,115,22,0.18)" : "transparent",
                  fontFamily: "JetBrains Mono, monospace",
                }}
              >
                {item === "login" ? "Login" : item === "register" ? "Register" : "Reset"}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" ? (
              <label className="block">
                <span className="block text-xs text-gray-400 mb-1.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>Name</span>
                <input
                  className={inputCls}
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Your name"
                />
              </label>
            ) : null}

            <label className="block">
              <span className="block text-xs text-gray-400 mb-1.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>Email</span>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="email"
                  required
                  className={`${inputCls} pl-9`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                />
              </div>
            </label>

            {mode !== "login" ? (
              <div>
                <span className="block text-xs text-gray-400 mb-1.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                  Verification code
                </span>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <ShieldCheck size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      className={`${inputCls} pl-9`}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="6-digit code"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={cooldown > 0}
                    className="px-3 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                    style={{ background: "#f97316" }}
                  >
                    {cooldown > 0 ? `${cooldown}s` : "Send OTP"}
                  </button>
                </div>
              </div>
            ) : null}

            <label className="block">
              <span className="block text-xs text-gray-400 mb-1.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                {mode === "reset" ? "New password" : "Password"}
              </span>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="password"
                  required
                  className={`${inputCls} pl-9`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
            </label>

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-white disabled:opacity-60"
              style={{ background: "linear-gradient(135deg, #f97316, #ea580c)" }}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              {mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Reset password"}
            </button>
          </form>

          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-4 text-sm text-gray-500 hover:text-gray-300"
          >
            Back to jobs
          </button>
        </div>
      </div>
    </main>
  );
}

const inputCls =
  "w-full px-3 py-2.5 rounded-lg bg-[#111827] border border-white/10 text-white text-sm focus:outline-none focus:border-orange-500/60 placeholder:text-gray-600";
